import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff, RefreshCw } from 'lucide-react';
import { createPortal } from 'react-dom';

interface NetworkErrorDialogProps {
  isOpen: boolean;
  onRetry?: () => void;
  onClose?: () => void;
}

export const NetworkErrorDialog: React.FC<NetworkErrorDialogProps> = ({
  isOpen,
  onClose,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const isMerchant = React.useMemo(() => {
    try {
      const profileStr = localStorage.getItem('offline_user_profile');
      if (profileStr) {
        const profile = JSON.parse(profileStr);
        return profile.activeRole === 'merchant';
      }
    } catch {
      return false;
    }
    return false;
  }, [isOpen]);

  const handleRetry = async () => {
    setIsRetrying(true);
    setRetryCount((prev) => prev + 1);

    try {
      // Prefer same-origin lightweight ping to avoid regional blocks
      const fetchWithTimeout = async (
        url: string,
        options: RequestInit & { method?: 'GET' | 'HEAD' } = {},
        timeoutMs = 6000,
      ) => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
          const response = await fetch(url, { ...options, signal: controller.signal });
          return response;
        } finally {
          clearTimeout(timer);
        }
      };

      const origin = window.location.origin;
      const probes = [
        `${origin}/manifest.webmanifest`,
        `${origin}/favicon.ico`,
        'https://www.gstatic.com/generate_204', // external fallback
      ];

      let ok = false;
      for (const url of probes) {
        try {
          let resp = await fetchWithTimeout(url, { method: 'HEAD', cache: 'no-store' }, 5000);
          if (!resp.ok || resp.status === 405) {
            resp = await fetchWithTimeout(url, { method: 'GET', cache: 'no-store' }, 5000);
          }
          if (resp.ok || resp.status === 204) {
            ok = true;
            break;
          }
        } catch {
          // try next probe
        }
      }

      if (ok) {
        // Connection restored
        if (onRetry) {
          onRetry();
        } else {
          // Default behavior - reload page
          window.location.reload();
        }
      } else {
        throw new Error('Still offline');
      }
    } catch {
      // Still offline, try page reload as fallback
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } finally {
      setIsRetrying(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const dialogContent = (
    <div className="fixed inset-0 z-[99999] transition-all duration-300 ease-out will-change-transform transform-gpu">
      <div className="absolute inset-0 bg-black/50 bg-opacity-60" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100">
          {/* Header */}
          <div className="p-8 text-center">
            {/* Icon with animation */}
            <div className="relative flex items-center justify-center w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-red-100 to-orange-100 rounded-full"></div>
              <div className="relative flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-full shadow-lg">
                <WifiOff className="w-8 h-8 text-white" />
              </div>
              {/* Ripple effect */}
              <div className="absolute inset-0 rounded-full border-2 border-red-300 opacity-20"></div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              {t('network.noConnection') || 'No Internet Connection'}
            </h3>

            {/* Description */}
            <p className="text-gray-600 mb-6 leading-relaxed">
              {t('network.checkConnection') ||
                'Please check your internet connection and try again.'}
            </p>
          </div>

          {/* Actions */}
          <div className="px-8 pb-8">
            <div className="flex flex-col space-y-3">
              {/* Primary Action - Retry */}
              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed"
              >
                <div className="flex items-center justify-center space-x-2">
                  <RefreshCw className={`w-5 h-5 ${isRetrying ? 'animate-spin' : ''}`} />
                  <span>
                    {isRetrying
                      ? t('network.retrying') || 'Retrying...'
                      : t('network.tryAgain') || 'Try Again'}
                  </span>
                </div>
              </button>

              {/* Secondary Action - Continue with Offline (Merchant Only) */}
              {onClose && isMerchant && (
                <button
                  onClick={onClose}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-all duration-200 border border-gray-200 hover:border-gray-300"
                >
                  {t('network.continueOffline') || 'Continue with Offline'}
                </button>
              )}
            </div>

            {/* Retry count indicator */}
            {retryCount > 0 && (
              <div className="mt-4 text-center">
                <span className="text-xs text-gray-400">
                  {t('network.retryAttempts') || 'Retry attempts'}: {retryCount}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
};

export default NetworkErrorDialog;
